import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { appointmentService } from '../services/appointmentService'
import { userService } from '../services/userService'
import { appointmentTypesService } from '../services/appointmentTypesService'
import { maternityService } from '../services/maternityService'
import { AppointmentFormData, AppointmentType } from '../types/appointment'
import {
  VISIT_REASON_CODES,
  VISIT_REASON_LABELS,
  isPregnancyRelatedReason,
  isOutcomeWithinMonths,
  POSTNATAL_LOOKBACK_MONTHS,
  type VisitReasonCode,
} from '../constants/visitReasons'
import { formatUserName } from '../utils/name'
import { PatientSearch } from './PatientSearch'
import './AppointmentScheduler.css'

// Calculate end time from start time and duration
const calculateEndTime = (startTime: string, duration: number): string => {
  if (!startTime) return ''
  const [hours, minutes] = startTime.split(':').map(Number)
  const endTime = new Date(2000, 0, 1, hours, minutes + duration)
  return `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`
}

interface AppointmentSchedulerProps {
  onSuccess?: () => void
  onCancel?: () => void
  initialPatientId?: string
  initialStaffId?: string
  initialDate?: string
  initialStartTime?: string
  initialDuration?: number
}

export function AppointmentScheduler({
  onSuccess,
  onCancel,
  initialPatientId,
  initialStaffId,
  initialDate,
  initialStartTime,
  initialDuration = 30,
}: AppointmentSchedulerProps) {
  const queryClient = useQueryClient()
  
  const [formData, setFormData] = useState<AppointmentFormData>({
    patientId: initialPatientId || '',
    staffId: initialStaffId || '',
    appointmentDate: initialDate || new Date().toISOString().split('T')[0],
    startTime: initialStartTime || '',
    endTime: initialStartTime ? calculateEndTime(initialStartTime, initialDuration) : '',
    duration: initialDuration,
    type: 'consultation',
    status: 'scheduled',
    reason: '',
    reasonCode: undefined,
    pregnancyId: undefined,
    notes: '',
  })

  // Update form data when initial values change
  useEffect(() => {
    if (initialStaffId || initialDate || initialStartTime || initialPatientId) {
      setFormData((prev) => {
        const newStartTime = initialStartTime || prev.startTime
        const newDuration = initialDuration || prev.duration
        return {
          ...prev,
          patientId: initialPatientId || prev.patientId,
          staffId: initialStaffId || prev.staffId,
          appointmentDate: initialDate || prev.appointmentDate,
          startTime: newStartTime,
          endTime: newStartTime ? calculateEndTime(newStartTime, newDuration) : prev.endTime,
          duration: newDuration,
        }
      })
    }
  }, [initialPatientId, initialStaffId, initialDate, initialStartTime, initialDuration])

  const { data: clinicalStaff, isLoading: staffLoading } = useQuery({
    queryKey: ['clinical-staff'],
    queryFn: () => userService.getClinicalStaff(),
  })

  const { data: availableSlots, isLoading: slotsLoading, error: slotsError } = useQuery({
    queryKey: ['available-slots', formData.staffId, formData.appointmentDate],
    queryFn: () => {
      console.log('[AppointmentScheduler] Fetching slots for:', { staffId: formData.staffId, date: formData.appointmentDate })
      return appointmentService.getAvailableSlots(formData.staffId, formData.appointmentDate)
    },
    enabled: !!formData.staffId && !!formData.appointmentDate,
  })

  const { data: appointmentTypes } = useQuery({
    queryKey: ['appointment-types'],
    queryFn: () => appointmentTypesService.getAll(),
  })

  // Update formData.type if the current type is no longer available
  useEffect(() => {
    if (appointmentTypes && appointmentTypes.length > 0) {
      const activeTypes = appointmentTypes.filter((type) => type.isActive)
      const currentTypeExists = activeTypes.some((type) => type.code === formData.type)
      if (!currentTypeExists && activeTypes.length > 0) {
        // Reset to first available type if current type is not available
        setFormData((prev) => ({ ...prev, type: activeTypes[0].code as AppointmentType }))
      }
    }
  }, [appointmentTypes, formData.type])

  // Fetch pregnancies when patient selected and reason is pregnancy-related
  const { data: pregnancies } = useQuery({
    queryKey: ['pregnancies', formData.patientId],
    queryFn: () => maternityService.getPregnancies(formData.patientId),
    enabled: !!formData.patientId && isPregnancyRelatedReason(formData.reasonCode as VisitReasonCode),
  })

  // Resolve pregnancy when pregnancies load and reason is antenatal/postnatal
  useEffect(() => {
    if (!isPregnancyRelatedReason(formData.reasonCode as VisitReasonCode)) {
      if (formData.pregnancyId) {
        setFormData((prev) => ({ ...prev, pregnancyId: undefined }))
      }
      return
    }
    if (!formData.patientId || !pregnancies) {
      return
    }
    let matched: { pregnancyId: string } | null = null
    if (formData.reasonCode === 'antenatal') {
      const active = pregnancies.find((p) => p.status === 'active')
      if (active) matched = { pregnancyId: active.pregnancyId }
    } else if (formData.reasonCode === 'postnatal') {
      const completed = pregnancies
        .filter((p) => p.status === 'completed' && isOutcomeWithinMonths(p.outcomeDate, POSTNATAL_LOOKBACK_MONTHS))
        .sort((a, b) => {
          const aDate = a.outcomeDate || a.updatedAt || ''
          const bDate = b.outcomeDate || b.updatedAt || ''
          return bDate.localeCompare(aDate)
        })[0]
      if (completed) matched = { pregnancyId: completed.pregnancyId }
    }
    setFormData((prev) => ({
      ...prev,
      pregnancyId: matched?.pregnancyId,
    }))
  }, [formData.patientId, formData.reasonCode, pregnancies])

  console.log('[AppointmentScheduler] Available slots:', { availableSlots, slotsLoading, slotsError, staffId: formData.staffId, date: formData.appointmentDate })

  const createMutation = useMutation({
    mutationFn: (data: AppointmentFormData) => appointmentService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      onSuccess?.()
    },
  })

  const handleTimeChange = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number)
    const endTime = new Date(2000, 0, 1, hours, minutes + formData.duration)
    const endTimeString = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`
    
    setFormData({
      ...formData,
      startTime: time,
      endTime: endTimeString,
    })
  }

  const handleDurationChange = (duration: number) => {
    if (formData.startTime) {
      const [hours, minutes] = formData.startTime.split(':').map(Number)
      const endTime = new Date(2000, 0, 1, hours, minutes + duration)
      const endTimeString = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`
      
      setFormData({
        ...formData,
        duration,
        endTime: endTimeString,
      })
    } else {
      setFormData({
        ...formData,
        duration,
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Prevent submission if the event originated from a nested form
    const target = e.target as HTMLElement
    if (target.closest('.quick-add-patient-form')) {
      e.stopPropagation()
      return
    }
    
    if (!formData.patientId || !formData.staffId || !formData.startTime) {
      alert('Please fill in all required fields')
      return
    }
    if (isPregnancyRelatedReason(formData.reasonCode as VisitReasonCode) && !formData.pregnancyId) {
      alert(
        formData.reasonCode === 'antenatal'
          ? 'Patient does not have an active pregnancy. Record a pregnancy before scheduling.'
          : `Patient does not have a completed pregnancy within ${POSTNATAL_LOOKBACK_MONTHS} months. Record the pregnancy outcome before scheduling.`
      )
      return
    }
    try {
      await createMutation.mutateAsync(formData)
    } catch (error: any) {
      alert(error?.message || 'Failed to create appointment')
    }
  }

  const selectedStaff = clinicalStaff?.find((s) => s.id === formData.staffId)

  return (
    <form className="appointment-scheduler" onSubmit={handleSubmit}>
      <div className="form-section">
        <div className="form-group form-group-full">
          <label htmlFor="patientId">
            Patient <span className="required">*</span>
          </label>
          <PatientSearch
            value={formData.patientId}
            onSelect={(patientId) => {
              setFormData((prev) => ({
                ...prev,
                patientId,
                pregnancyId: patientId !== prev.patientId ? undefined : prev.pregnancyId,
              }))
            }}
            placeholder="Search by name, phone, patient number, PPS, or GMS..."
            required
          />
        </div>
      </div>

      <div className="form-section form-section-two-col">
        <div className="form-group">
          <label htmlFor="staffId">
            Clinical Staff <span className="required">*</span>
          </label>
          <select
            id="staffId"
            value={formData.staffId}
            onChange={(e) => setFormData({ ...formData, staffId: e.target.value, startTime: '', endTime: '' })}
            required
            disabled={staffLoading}
          >
            <option value="">Select clinical staff...</option>
            {clinicalStaff?.map((staff) => (
              <option key={staff.id} value={staff.id}>
                {formatUserName(staff as any)} ({staff.role})
              </option>
            ))}
          </select>
          {selectedStaff && (
            <p className="field-hint">
              {selectedStaff.roleCategory} • {selectedStaff.email}
            </p>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="type">Appointment Type</label>
          <select
            id="type"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as AppointmentType })}
          >
            {appointmentTypes && appointmentTypes.length > 0 ? (
              appointmentTypes
                .filter((type) => type.isActive)
                .map((type) => (
                  <option key={type.id} value={type.code}>
                    {type.name}
                  </option>
                ))
            ) : (
              <>
                <option value="consultation">Consultation</option>
                <option value="follow-up">Follow-up</option>
                <option value="checkup">Checkup</option>
                <option value="procedure">Procedure</option>
                <option value="emergency">Emergency</option>
                <option value="other">Other</option>
              </>
            )}
          </select>
        </div>
      </div>

      <div className="form-section form-section-three-col">
        <div className="form-group">
          <label htmlFor="appointmentDate">
            Date <span className="required">*</span>
          </label>
          <input
            id="appointmentDate"
            type="date"
            value={formData.appointmentDate}
            onChange={(e) => setFormData({ ...formData, appointmentDate: e.target.value, startTime: '', endTime: '' })}
            min={new Date().toISOString().split('T')[0]}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="startTime">
            Start Time <span className="required">*</span>
          </label>
          {(() => {
            // Include current/initial start time in options when it's not in the API list (e.g. slot after a closed period)
            const baseSlots = availableSlots || []
            const currentStart = formData.startTime
            const timeOptions =
              currentStart && !baseSlots.includes(currentStart)
                ? [...new Set([...baseSlots, currentStart])].sort()
                : baseSlots
            return timeOptions.length > 0 ? (
              <>
                <select
                  id="startTime"
                  value={formData.startTime}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  required
                  disabled={slotsLoading || !formData.staffId || !formData.appointmentDate}
                >
                  <option value="">Select available time...</option>
                  {timeOptions.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
                <p className="field-hint">
                  {timeOptions.length} available {timeOptions.length === 1 ? 'slot' : 'slots'}
                </p>
              </>
            ) : (
              <input
                id="startTime"
                type="time"
                value={formData.startTime}
                onChange={(e) => handleTimeChange(e.target.value)}
                required
                disabled={slotsLoading || !formData.staffId || !formData.appointmentDate}
              />
            )
          })()}
          {formData.endTime && (
            <p className="field-hint">Ends: {formData.endTime}</p>
          )}
          {formData.staffId && formData.appointmentDate && !slotsLoading && availableSlots && availableSlots.length === 0 && (
            <p className="field-hint error">No available slots</p>
          )}
          {slotsLoading && formData.staffId && formData.appointmentDate && (
            <p className="field-hint">Loading...</p>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="duration">
            Duration <span className="required">*</span>
          </label>
          <select
            id="duration"
            value={formData.duration}
            onChange={(e) => handleDurationChange(Number(e.target.value))}
            required
          >
            <option value="15">15 min</option>
            <option value="30">30 min</option>
            <option value="45">45 min</option>
            <option value="60">60 min</option>
            <option value="90">90 min</option>
            <option value="120">120 min</option>
          </select>
        </div>
      </div>

      <div className="form-section form-section-two-col">
        <div className="form-group">
          <label htmlFor="reasonCode">Reason for Visit</label>
          <select
            id="reasonCode"
            value={formData.reasonCode || ''}
            onChange={(e) => {
              const code = (e.target.value || undefined) as VisitReasonCode | undefined
              setFormData((prev) => ({
                ...prev,
                reasonCode: code,
                reason: code === 'other' ? prev.reason : undefined,
                pregnancyId: !isPregnancyRelatedReason(code) ? undefined : prev.pregnancyId,
              }))
            }}
          >
            <option value="">Select a reason (optional)</option>
            {VISIT_REASON_CODES.map((code) => (
              <option key={code} value={code}>
                {VISIT_REASON_LABELS[code]}
              </option>
            ))}
          </select>
          {formData.reasonCode === 'other' && (
            <input
              type="text"
              value={formData.reason || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="Enter reason..."
              className="reason-other-input"
              style={{ marginTop: '0.5rem' }}
            />
          )}
          {isPregnancyRelatedReason(formData.reasonCode as VisitReasonCode) && formData.patientId && (
            <p className="field-hint">
              {formData.pregnancyId
                ? `Linked to ${formData.reasonCode === 'antenatal' ? 'active' : 'last completed'} pregnancy`
                : formData.reasonCode === 'antenatal'
                  ? 'Patient does not have an active pregnancy. Record a pregnancy before scheduling.'
                  : `Patient does not have a completed pregnancy within ${POSTNATAL_LOOKBACK_MONTHS} months. Record the pregnancy outcome before scheduling.`}
            </p>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={2}
            placeholder="Additional notes..."
          />
        </div>
      </div>

      <div className="form-actions">
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn-primary"
          disabled={
            createMutation.isPending ||
            !formData.patientId ||
            !formData.staffId ||
            !formData.startTime ||
            (isPregnancyRelatedReason(formData.reasonCode as VisitReasonCode) && !formData.pregnancyId)
          }
        >
          {createMutation.isPending ? 'Scheduling...' : 'Schedule Appointment'}
        </button>
      </div>
    </form>
  )
}

